import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';

const emptyClientPatch = {
    clientOrganizationId: null,
    clientWorkplaceId: null,
    affiliatedOrganizationName: '',
    affiliatedOrganizationPhone: '',
    affiliatedOrganizationEmail: '',
    affiliatedOrganizationContact: '',
    workplaceName: '',
    workplaceAddress: '',
    workplacePhone: '',
    workplaceEmail: '',
    workplaceContact: '',
    workplaceContactTitle: '',
};

const normalizeId = (value) => value || '';

const buildOrganizationPatch = (organization) => ({
    clientOrganizationId: organization?.id || null,
    clientWorkplaceId: null,
    affiliatedOrganizationName: organization?.name || '',
    affiliatedOrganizationPhone: organization?.phone || '',
    affiliatedOrganizationEmail: organization?.email || '',
    affiliatedOrganizationContact: organization?.contact || '',
    workplaceName: '',
    workplaceAddress: '',
    workplacePhone: '',
    workplaceEmail: '',
    workplaceContact: '',
    workplaceContactTitle: '',
});

const buildWorkplacePatch = (workplace) => ({
    clientWorkplaceId: workplace?.id || null,
    workplaceName: workplace?.name || '',
    workplaceAddress: workplace?.address || '',
    workplacePhone: workplace?.phone || '',
    workplaceEmail: workplace?.email || '',
    workplaceContact: workplace?.contact || '',
    workplaceContactTitle: workplace?.contactTitle || '',
});

export default function ClientSelectionFields({
    value = {},
    onChange,
    variant = 'stacked',
}) {
    const { t } = useTranslation();
    const [clientOptions, setClientOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState('');

    const text = (key, fallback) => {
        const translated = t(key);
        return translated === key ? fallback : translated;
    };

    useEffect(() => {
        let isActive = true;
        setIsLoading(true);
        setLoadError('');

        apiService.getClientOptions()
            .then((items) => {
                if (isActive) {
                    setClientOptions(Array.isArray(items) ? items : []);
                }
            })
            .catch((error) => {
                if (isActive) {
                    setLoadError(error.message || '読み込みに失敗しました');
                }
            })
            .finally(() => {
                if (isActive) {
                    setIsLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, []);

    const selectedOrganization = useMemo(() => (
        clientOptions.find(organization => organization.id === value.clientOrganizationId) || null
    ), [clientOptions, value.clientOrganizationId]);

    const workplaces = useMemo(() => (
        selectedOrganization?.workplaces || []
    ), [selectedOrganization]);

    const selectedWorkplace = useMemo(() => (
        workplaces.find(workplace => workplace.id === value.clientWorkplaceId) || null
    ), [workplaces, value.clientWorkplaceId]);

    const emitChange = (patch) => {
        if (typeof onChange === 'function') {
            onChange(patch);
        }
    };

    const handleOrganizationChange = (event) => {
        const organizationId = event.target.value;
        if (!organizationId) {
            emitChange(emptyClientPatch);
            return;
        }

        const organization = clientOptions.find(item => item.id === organizationId);
        emitChange(buildOrganizationPatch(organization));
    };

    const handleWorkplaceChange = (event) => {
        const workplaceId = event.target.value;
        if (!workplaceId) {
            emitChange({
                clientWorkplaceId: null,
                workplaceName: '',
                workplaceAddress: '',
                workplacePhone: '',
                workplaceEmail: '',
                workplaceContact: '',
                workplaceContactTitle: '',
            });
            return;
        }

        const workplace = workplaces.find(item => item.id === workplaceId);
        emitChange(buildWorkplacePatch(workplace));
    };

    const organizationSnapshot = selectedOrganization || {
        name: value.affiliatedOrganizationName,
        phone: value.affiliatedOrganizationPhone,
        email: value.affiliatedOrganizationEmail,
        contact: value.affiliatedOrganizationContact,
    };

    const workplaceSnapshot = selectedWorkplace || {
        name: value.workplaceName,
        address: value.workplaceAddress,
        phone: value.workplacePhone,
        email: value.workplaceEmail,
        contact: value.workplaceContact,
        contactTitle: value.workplaceContactTitle,
    };

    const infoRows = [
        { label: t('organizationName'), value: selectedOrganization ? organizationSnapshot.name : '' },
        { label: t('organizationPhone'), value: selectedOrganization ? organizationSnapshot.phone : '' },
        { label: t('organizationEmail'), value: selectedOrganization ? organizationSnapshot.email : '' },
        { label: t('organizationContact'), value: selectedOrganization ? organizationSnapshot.contact : '' },
        { label: t('workplaceName'), value: selectedWorkplace ? workplaceSnapshot.name : '' },
        { label: t('workplaceAddress'), value: selectedWorkplace ? workplaceSnapshot.address : '' },
        { label: t('workplacePhone'), value: selectedWorkplace ? workplaceSnapshot.phone : '' },
        { label: t('workplaceEmail'), value: selectedWorkplace ? workplaceSnapshot.email : '' },
        { label: t('workplaceContact'), value: selectedWorkplace ? workplaceSnapshot.contact : '' },
        { label: t('workplaceContactTitle'), value: selectedWorkplace ? workplaceSnapshot.contactTitle : '' },
    ];

    const isRows = variant === 'rows3' || variant === 'rows5';
    const isRows5 = variant === 'rows5';
    const rowClass = isRows
        ? `grid ${isRows5 ? 'grid-cols-5' : 'grid-cols-3'} gap-4 py-3 border-b border-gray-100`
        : 'space-y-1';
    const labelClass = isRows
        ? `${isRows5 ? 'col-span-2' : ''} text-sm font-medium text-gray-700 flex items-center`
        : 'block text-sm font-medium text-gray-700';
    const fieldClass = isRows ? (isRows5 ? 'col-span-3' : 'col-span-2') : '';
    const selectClass = 'block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';

    const renderSelectRow = ({ label, id, value: selectValue, onSelectChange, disabled, children }) => (
        <div className={rowClass}>
            <label htmlFor={id} className={labelClass}>{label}</label>
            <div className={fieldClass}>
                <select
                    id={id}
                    name={id}
                    value={selectValue}
                    onChange={onSelectChange}
                    disabled={disabled}
                    className={selectClass}
                >
                    {children}
                </select>
            </div>
        </div>
    );

    const renderInfo = () => {
        if (!selectedOrganization) {
            return (
                <div className={isRows ? rowClass : 'rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-sm text-gray-500'}>
                    {isRows ? (
                        <>
                            <div className={labelClass}>{t('affiliatedOrganization')}</div>
                            <div className={`${fieldClass} text-sm text-gray-500`}>
                                {text('clientSelection.noSelection', '所属機関を選択すると取引先情報を表示します。')}
                            </div>
                        </>
                    ) : (
                        text('clientSelection.noSelection', '所属機関を選択すると取引先情報を表示します。')
                    )}
                </div>
            );
        }

        if (isRows) {
            return (
                <div className="mt-2">
                    {infoRows.map(row => (
                        <div key={row.label} className={rowClass}>
                            <div className={labelClass}>{row.label}</div>
                            <div className={`${fieldClass} text-sm text-gray-900 flex items-center`}>
                                {row.value || '---'}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                    {infoRows.map(row => (
                        <div key={row.label} className="min-w-0">
                            <div className="text-xs font-medium text-gray-500">{row.label}</div>
                            <div className="mt-1 break-words text-sm text-gray-900">{row.value || '---'}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className={isRows ? '' : 'space-y-4'}>
            {renderSelectRow({
                label: t('organizationName'),
                id: 'clientOrganizationId',
                value: selectedOrganization ? normalizeId(value.clientOrganizationId) : '',
                onSelectChange: handleOrganizationChange,
                disabled: isLoading,
                children: (
                    <>
                        <option value="">{text('clientSelection.unselected', '未選択')}</option>
                        {clientOptions.map(organization => (
                            <option key={organization.id} value={organization.id}>
                                {organization.name}
                            </option>
                        ))}
                    </>
                )
            })}

            {renderSelectRow({
                label: t('workplaceName'),
                id: 'clientWorkplaceId',
                value: selectedWorkplace ? normalizeId(value.clientWorkplaceId) : '',
                onSelectChange: handleWorkplaceChange,
                disabled: isLoading || !selectedOrganization || workplaces.length === 0,
                children: (
                    <>
                        <option value="">{text('clientSelection.unselected', '未選択')}</option>
                        {workplaces.map(workplace => (
                            <option key={workplace.id} value={workplace.id}>
                                {workplace.name}
                            </option>
                        ))}
                    </>
                )
            })}

            {isLoading && (
                <div className={isRows ? `${rowClass} text-sm text-gray-500` : 'text-sm text-gray-500'}>
                    {isRows ? <><div className={labelClass}></div><div className={fieldClass}>{t('loading')}</div></> : t('loading')}
                </div>
            )}

            {loadError && (
                <div className={isRows ? `${rowClass} text-sm text-red-600` : 'text-sm text-red-600'}>
                    {isRows ? <><div className={labelClass}></div><div className={fieldClass}>{loadError}</div></> : loadError}
                </div>
            )}

            {renderInfo()}
        </div>
    );
}
